'use client';

/**
 * app/help/_components/HelpFaqClient.tsx
 *
 * Client Component — handles mobile-collapsed FAQ categories + sticky nav (Cycle 5).
 * Updated: Cycle 6 — category header restructure, exclusive-open accordion, divider removal.
 *
 * Behaviour matrix:
 *   Desktop (>=768px): All items rendered with controlled <details> (exclusive-open state).
 *     Category headers show numeral+label row + category heading + item count.
 *   Mobile (<=767px): Category-level collapse. All 4 category cards show on load.
 *     Tap a category card to expand its items (one category open at a time).
 *     Sticky category nav appears while scrolling inside an expanded category.
 *
 * Item-level accordion exclusive-open (Cycle 6):
 *   Only one FAQ item is open at a time across the entire page (all categories).
 *   openItemSlug state lives in this component. Each <details> is controlled:
 *   open={openItemSlug === item.slug}. Clicking sets openItemSlug to the item's
 *   slug (or null if already open). Close + open animations run simultaneously
 *   (not sequential) — both at 300ms cubic-bezier(0.0, 0, 0.2, 1).
 *
 * Category header structure (Cycle 6 — replaces eyebrow+heading from Cycle 5):
 *   Row 1: numeral+label row — "01  FIRST STEPS" (CG 600, --text-h3, --gold, 0.22em tracking)
 *   Row 2: category heading — "Getting Started & Access" (CG 500, --text-h3, --text)
 *   Row 3: item count — "5 questions" (Figtree 400, --text-label, --text-muted)
 *
 * Category dividers (Cycle 5 Move 3) RETIRED. The .help-category-divider DOM elements
 * are removed. Section boundary is communicated by tonal banding + numeral+label row.
 *
 * Tonal banding:
 *   Categories alternate --bg (Band A) and --bg-2 (Band B) backgrounds.
 *   FAQ item card backgrounds are one step above the band
 *   (--bg-2 on Band A, --bg-3 on Band B).
 *
 * Added: Vela, feat/help-page-migration-2026-05-12 (Cycle 5)
 * Updated: Vela, feat/help-page-migration-2026-05-12 (Cycle 6)
 * Governance: SOP-WEBSITE-02, SOP-BRAND-01, SOP-WEBSITE-03, Aria C5/C6
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';

// ── Types ──────────────────────────────────────────────────────────────────────

interface FaqItemData {
  slug: string;
  question: string;
  answerHtml: string; // pre-rendered HTML string from RSC
  sourcePageLink?: string;
}

interface CategoryData {
  slug: string;
  label: string;
  eyebrow: string;    // Cycle 5 field — kept for type compat; unused in Cycle 6 header rendering
  numeral: string;    // "01" | "02" | "03" | "04" — new in Cycle 6
  numeralLabel: string; // "FIRST STEPS" etc. — new in Cycle 6 (uppercase label for Row 1)
  items: FaqItemData[];
}

interface HelpFaqClientProps {
  categories: CategoryData[];
}

// ── Pill component ─────────────────────────────────────────────────────────────

function SourcePill({ sourcePageLink }: { sourcePageLink: string }) {
  if (sourcePageLink.includes('/pro-member')) {
    return (
      <Link href={sourcePageLink} className="help-faq-item__pro-pill">
        Read more on the Pro Member page
      </Link>
    );
  }
  if (sourcePageLink.includes('zoom-audio')) {
    return (
      <Link
        href="/help/zoom-audio-settings-for-music-lessons"
        className="help-faq-item__pro-pill help-faq-item__pro-pill--guide"
      >
        Read the full guide
      </Link>
    );
  }
  return (
    <Link href={sourcePageLink} className="help-faq-item__pro-pill">
      Read more
    </Link>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function HelpFaqClient({ categories }: HelpFaqClientProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [openCategorySlug, setOpenCategorySlug] = useState<string | null>(null);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [stickyLabel, setStickyLabel] = useState('');

  // Cycle 6 — exclusive-open accordion: only one FAQ item open at a time, across all categories.
  // null = all closed. Setting to a slug closes all others simultaneously (both animations run
  // in parallel at 300ms — not sequential).
  const [openItemSlug, setOpenItemSlug] = useState<string | null>(null);

  // Ref to the category header sentinel elements for IntersectionObserver
  const sentinelRefs = useRef<Map<string, HTMLElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Detect mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // When mobile collapses or user switches category, tear down sticky
  useEffect(() => {
    if (!isMobile || openCategorySlug === null) {
      setStickyVisible(false);
    }
  }, [isMobile, openCategorySlug]);

  // IntersectionObserver: sticky nav appears when category header scrolls out of view
  const registerSentinel = useCallback((slug: string, el: HTMLElement | null) => {
    if (el) {
      sentinelRefs.current.set(slug, el);
    } else {
      sentinelRefs.current.delete(slug);
    }
  }, []);

  useEffect(() => {
    if (!isMobile || openCategorySlug === null) {
      observerRef.current?.disconnect();
      setStickyVisible(false);
      return;
    }

    const sentinel = sentinelRefs.current.get(openCategorySlug);
    if (!sentinel) return;

    const openCat = categories.find(c => c.slug === openCategorySlug);
    const label = openCat?.label ?? '';

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setStickyLabel(label);
          setStickyVisible(true);
        } else {
          setStickyVisible(false);
        }
      },
      { rootMargin: '-64px 0px 0px 0px', threshold: 0 } // 64px offset for site nav
    );
    observerRef.current.observe(sentinel);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [isMobile, openCategorySlug, categories]);

  const handleCategoryTap = (slug: string) => {
    setOpenCategorySlug(prev => (prev === slug ? null : slug));
  };

  // Cycle 6 — exclusive-open: toggle item open/closed; closing all others simultaneously.
  // Both the close (previously-open item) and open (newly-tapped item) animations
  // run in parallel — not sequential. 300ms timing from existing Cycle 5 CSS.
  const handleItemToggle = (slug: string) => {
    setOpenItemSlug(prev => (prev === slug ? null : slug));
  };

  const handleBackToCategories = () => {
    setOpenCategorySlug(null);
    setStickyVisible(false);
    // Scroll to FAQ H2 (id="help-faq-start" — moved to H2 in page.tsx in Cycle 6)
    const faqEl = document.getElementById('help-faq-start');
    if (faqEl) {
      faqEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const bandClasses = ['faq-band-a', 'faq-band-b', 'faq-band-a', 'faq-band-b'];

  return (
    <>
      {/* Sticky category nav — mobile only */}
      {isMobile && (
        <div
          className={`help-sticky-cat-nav${stickyVisible ? ' help-sticky-cat-nav--visible' : ''}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="help-sticky-cat-nav__label">{stickyLabel}</span>
          <button
            className="help-sticky-cat-nav__back"
            onClick={handleBackToCategories}
            type="button"
            aria-label="Back to all categories"
          >
            All categories
          </button>
        </div>
      )}

      {categories.map((cat, catIndex) => {
        const isOpen = !isMobile || openCategorySlug === cat.slug;
        const bandClass = bandClasses[catIndex];

        return (
          <React.Fragment key={cat.slug}>
            {/*
              Cycle 6: CategoryDivider (.help-category-divider) RETIRED.
              Section boundary is communicated by tonal banding + the numeral+label row
              that opens each category section. No DOM element between sections.
            */}

            <section
              id={`cat-${cat.slug}`}
              className={`help-faq-section ${bandClass}${isMobile && openCategorySlug === cat.slug ? ' help-faq-section--mobile-open' : ''}`}
              aria-labelledby={`cat-heading-${cat.slug}`}
              style={{ scrollMarginTop: '80px' }}
            >
              <div className="container help-faq-section__inner">

                {/* Desktop: static category header. Mobile: tappable category card */}
                {isMobile ? (
                  <button
                    className={`help-cat-card${isOpen ? ' help-cat-card--open' : ''}`}
                    onClick={() => handleCategoryTap(cat.slug)}
                    aria-expanded={isOpen}
                    aria-controls={`cat-items-${cat.slug}`}
                    type="button"
                    ref={(el) => registerSentinel(cat.slug, el)}
                  >
                    <div className="help-cat-card__left">
                      {/* Cycle 6: numeral+label row replaces old .help-cat-card__eyebrow */}
                      <span className="help-cat-card__numeral-label" aria-hidden="true">
                        {cat.numeral}&nbsp;&nbsp;{cat.numeralLabel}
                      </span>
                      <span
                        id={`cat-heading-${cat.slug}`}
                        className="help-cat-card__name"
                      >
                        {cat.label}
                      </span>
                      <span className="help-cat-card__count">
                        {cat.items.length} question{cat.items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="help-cat-card__toggle" aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                ) : (
                  <div
                    className="help-faq-category__header"
                    ref={(el) => registerSentinel(cat.slug, el)}
                  >
                    {/* Cycle 6: Row 1 — numeral+label. Row 2 — category heading. Row 3 — item count. */}
                    <p className="help-faq-category__numeral-label" aria-hidden="true">
                      {cat.numeral}&nbsp;&nbsp;{cat.numeralLabel}
                    </p>
                    <h2
                      id={`cat-heading-${cat.slug}`}
                      className="help-faq-category__heading"
                    >
                      {cat.label}
                    </h2>
                    <p className="help-faq-category__count">
                      {cat.items.length} question{cat.items.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                {/* Items list */}
                <ul
                  id={`cat-items-${cat.slug}`}
                  className={`help-faq-category__list${isMobile && !isOpen ? ' help-faq-category__list--hidden' : ''}`}
                  role="list"
                >
                  {cat.items.map(item => {
                    const itemIsOpen = openItemSlug === item.slug;
                    return (
                      <li
                        key={item.slug}
                        className={`help-faq-item help-faq-item--band-${catIndex % 2 === 0 ? 'a' : 'b'}${itemIsOpen ? ' help-faq-item--open' : ''}`}
                      >
                        {/*
                          Cycle 6 — controlled <details> for exclusive-open behaviour.
                          Native <details> open attribute is driven by React state (openItemSlug).
                          onClick on <summary> calls handleItemToggle to update parent state,
                          which re-renders all items — closing the previously-open one and
                          opening the newly-tapped one simultaneously (both 300ms animations run
                          in parallel, not sequential).
                          e.preventDefault() blocks the native toggle so state is the sole source.
                        */}
                        <details
                          id={`faq-${item.slug}`}
                          className="help-faq-item__details"
                          open={itemIsOpen}
                        >
                          <summary
                            className="help-faq-item__summary"
                            onClick={(e) => {
                              e.preventDefault();
                              handleItemToggle(item.slug);
                            }}
                          >
                            <span className={`help-faq-item__question${itemIsOpen ? ' help-faq-item__question--open' : ''}`}>
                              {item.question}
                            </span>
                            {/* + / − toggle: driven by itemIsOpen React state */}
                            <span className="help-faq-item__toggle" aria-hidden="true">
                              <span className="help-faq-item__toggle-plus"
                                style={{ display: itemIsOpen ? 'none' : 'inline' }}>+</span>
                              <span className="help-faq-item__toggle-minus"
                                style={{ display: itemIsOpen ? 'inline' : 'none' }}>−</span>
                            </span>
                          </summary>

                          <div className="help-faq-item__body">
                            <div
                              className="help-faq-item__answer"
                              // HTML is pre-rendered server-side by page.tsx renderRichText
                              dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                            />
                            {item.sourcePageLink && (
                              <div className="help-faq-item__pill-row">
                                <SourcePill sourcePageLink={item.sourcePageLink} />
                              </div>
                            )}
                          </div>
                        </details>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          </React.Fragment>
        );
      })}
    </>
  );
}
